import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import BaseCard from '../common/layout/BaseCard';
import { Button } from '../common/ui/Button';
import Pill from '../common/display/Pill';
import Avatar from '../common/display/Avatar';
import { useAuth } from '../../lib/AuthContext';
import { captureAppException, createSupabaseError } from '../../utils/errorCaptureUtils';
import { NotFound } from '../common/layout/NotFound';
import { TaxonomyData, Workspace, WorkspaceTaxonomies } from './types';

export const WorkspacePage = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taxonomyData, setTaxonomyData] = useState<TaxonomyData>({});
  const [workspaceTaxonomies, setWorkspaceTaxonomies] = useState<WorkspaceTaxonomies>({
    countries: [],
    industries: [],
    platforms: [],
    programming_languages: [],
    tools: [],
    sectors: [],
    subsectors: [],
  });

  useEffect(() => {
    const fetchTaxonomyData = async () => {
      try {
        const [
          { data: countries },
          { data: industries },
          { data: platforms },
          { data: programmingLanguages },
          { data: tools },
          { data: sectors },
          { data: subsectors },
        ] = await Promise.all([
          supabase.from('countries').select('id, name'),
          supabase.from('industries').select('id, name'),
          supabase.from('platforms').select('id, name'),
          supabase.from('programming_languages').select('id, name'),
          supabase.from('tools').select('id, name'),
          supabase.from('sectors').select('id, name'),
          supabase.from('subsectors').select('id, name'),
        ]);

        setTaxonomyData({
          countries: countries || [],
          industries: industries || [],
          platforms: platforms || [],
          programming_languages: programmingLanguages || [],
          tools: tools || [],
          sectors: sectors || [],
          subsectors: subsectors || [],
        });
      } catch (error) {
        captureAppException(error, {
          tags: {
            context: 'workspace_page',
            workspace_id: id,
            action: 'fetch_taxonomy_data'
          }
        });
        console.error('Error fetching taxonomy data:', error);
      }
    };

    fetchTaxonomyData();
  }, []);

  useEffect(() => {
    const fetchWorkspace = async () => {
      try {
        const { data, error } = await supabase
          .from('workspaces')
          .select(`
            *,
            workspace_countries(country_id),
            workspace_industries(industry_id),
            workspace_platforms(platform_id),
            workspace_programming_languages(programming_language_id),
            workspace_tools(tool_id),
            workspace_sectors(sector_id),
            workspace_subsectors(subsector_id),
            workspace_users (
              user_id,
              role,
              seat_type,
              joined_at,
              users (
                first_name,
                last_name,
                profile_image_url,
                job_title,
                organization
              )
            )
          `)
          .eq('workspace_id', id)
          .single();

        if (error) throw createSupabaseError(error, 'fetch_workspace');

        // Extract taxonomy relationships
        if (data) {
          setWorkspaceTaxonomies({
            countries: data.workspace_countries?.map((wc: any) => wc.country_id) || [],
            industries: data.workspace_industries?.map((wi: any) => wi.industry_id) || [],
            platforms: data.workspace_platforms?.map((wp: any) => wp.platform_id) || [],
            programming_languages: data.workspace_programming_languages?.map((wpl: any) => wpl.programming_language_id) || [],
            tools: data.workspace_tools?.map((wt: any) => wt.tool_id) || [],
            sectors: data.workspace_sectors?.map((ws: any) => ws.sector_id) || [],
            subsectors: data.workspace_subsectors?.map((ws: any) => ws.subsector_id) || [],
          });
        }

        setWorkspace(data);
      } catch (err) {
        captureAppException(err, {
          tags: {
            context: 'workspace_page',
            workspace_id: id,
            action: 'fetch_workspace'
          }
        });
        setError(err instanceof Error ? err.message : 'Failed to fetch workspace');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchWorkspace();
    }
  }, [id]);

  // Add a function to check if user has management permissions
  const canManageWorkspace = (workspace: Workspace) => {
    const currentUserRole = workspace.workspace_users?.find(
      wu => wu.user_id === user?.id
    )?.role;
    return currentUserRole === 'owner' || currentUserRole === 'editor';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <NotFound 
        title="Workspace Not Found"
        message={error || "The workspace you're looking for doesn't exist or has been removed."}
      />
    );
  }

  return (
    <div className="space-y-6">
      <BaseCard>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4">
              <Avatar
                firstName={workspace.workspace_name.charAt(0)}
                lastName=""
                profileImageUrl={workspace.logo_url}
                size="medium"
              />
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {workspace.workspace_name}
                  </h1>
                  <Pill
                    text={workspace.workspace_type === 'personal' ? 'Personal' : 'Team'}
                    className={workspace.workspace_type === 'personal'
                      ? 'bg-secondary-100 text-secondary-base'
                      : 'bg-primary-100 text-primary-base'
                    }
                  />
                </div>
                {/* Removed the description from here */}
              </div>
            </div>
          </div>
          {workspace && canManageWorkspace(workspace) && (
            <div className="flex gap-2 self-start md:self-auto">
              <Button
                onClick={() => navigate(`/workspace/${workspace.workspace_id}/manage`)}
                variant="outline"
                size="small"
              >
                Manage Workspace
              </Button>
            </div>
          )}
        </div>
      </BaseCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Bio and Experience */}
        <div className="flex flex-col gap-6">
          {/* Bio Card */}
          <BaseCard className="bg-primary-base text-white">
            <h2 className="text-lg font-bold text-white mb-4">About</h2>
            {workspace.description ? (
              <p className="text-white/90">{workspace.description}</p>
            ) : (
              <p className="text-white/60 italic">No description provided</p>
            )}
          </BaseCard>

          {/* Experience & Interests Card */}
          <BaseCard>
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">Experience & Interests</h2>
                {workspaceTaxonomies.platforms.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Platforms</h3>
                    <div className="flex flex-wrap gap-2">
                      {workspaceTaxonomies.platforms.map((platformId) => (
                        <Pill
                          key={platformId}
                          text={taxonomyData.platforms?.find(p => p.id === platformId)?.name || ''}
                          className="bg-gray-100 text-gray-700"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {workspaceTaxonomies.tools.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Tools</h3>
                    <div className="flex flex-wrap gap-2">
                      {workspaceTaxonomies.tools.map((toolId) => (
                        <Pill
                          key={toolId}
                          text={taxonomyData.tools?.find(t => t.id === toolId)?.name || ''}
                          className="bg-gray-100 text-gray-700"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {workspaceTaxonomies.programming_languages.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Programming Languages</h3>
                    <div className="flex flex-wrap gap-2">
                      {workspaceTaxonomies.programming_languages.map((langId) => (
                        <Pill
                          key={langId}
                          text={taxonomyData.programming_languages?.find(l => l.id === langId)?.name || ''}
                          className="bg-gray-100 text-gray-700"
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {workspaceTaxonomies.sectors.map((sectorId) => (
                    <Pill
                      key={sectorId}
                      text={taxonomyData.sectors?.find(s => s.id === sectorId)?.name || ''}
                      className="bg-blue-100 text-blue-700 rounded-full text-sm"
                    />
                  ))}
                  {workspaceTaxonomies.subsectors.map((subsectorId) => (
                    <Pill
                      key={subsectorId}
                      text={taxonomyData.subsectors?.find(s => s.id === subsectorId)?.name || ''}
                      className="bg-blue-50 text-blue-600 rounded-full text-sm"
                    />
                  ))}
                </div>
              </div>
            </div>
          </BaseCard>
        </div>

        {/* Middle Column - Details */}
        <div>
          <BaseCard className="h-full">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Details</h2>
            {workspace.organization_type && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500">Organization Type</h3>
                <p className="text-gray-900 capitalize">
                  {workspace.organization_type.replace(/_/g, ' ')}
                </p>
              </div>
            )}
            {workspace.organization_size && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500">Organization Size</h3>
                <p className="text-gray-900">
                  {workspace.organization_size === '0' ? '1' : workspace.organization_size}+ employees
                </p>
              </div>
            )}
            {workspace.founded_year && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500">Founded</h3>
                <p className="text-gray-900">{workspace.founded_year}</p>
              </div>
            )}
            {workspaceTaxonomies.countries.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500">Countries</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  {workspaceTaxonomies.countries.map((countryId) => (
                    <Pill
                      key={countryId}
                      text={taxonomyData.countries?.find(c => c.id === countryId)?.name || ''}
                      className="bg-gray-100 text-gray-700"
                    />
                  ))}
                </div>
              </div>
            )}
            {workspaceTaxonomies.industries.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500">Industries</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  {workspaceTaxonomies.industries.map((industryId) => (
                    <Pill
                      key={industryId}
                      text={taxonomyData.industries?.find(i => i.id === industryId)?.name || ''}
                      className="bg-gray-100 text-gray-700"
                    />
                  ))}
                </div>
              </div>
            )}
          </BaseCard>
        </div>

        {/* Right Column - Members */}
        <div>
          <BaseCard className="h-full">
            <div className="h-full flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-900">Members</h2>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto">
                {workspace.workspace_users?.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => navigate(`/profile/${member.user_id}`)}
                        className="hover:opacity-80 transition-opacity"
                      >
                        <Avatar
                          firstName={member.users?.first_name || ''}
                          lastName={member.users?.last_name || ''}
                          profileImageUrl={member.users?.profile_image_url}
                          size="medium"
                        />
                      </button>
                      <div>
                        <button
                          onClick={() => navigate(`/profile/${member.user_id}`)}
                          className="text-left hover:text-primary-base transition-colors"
                        >
                          <h3 className="font-medium text-gray-900">
                            {member.users?.first_name} {member.users?.last_name}
                          </h3>
                          {member.users?.job_title && (
                            <p className="text-sm text-gray-500">{member.users.job_title}</p>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Pill
                        text={member.role}
                        className="capitalize bg-gray-100 text-gray-700"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BaseCard>
        </div>
      </div>
    </div>
  );
};